import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { brandLibraryAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Plus, 
  BookOpen, 
  Edit,
  Trash2,
  FileText,
  AlertCircle,
  MessageSquare,
  Tag,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';

const ITEM_TYPES = [
  { value: 'tone', label: 'Tone of Voice', icon: MessageSquare },
  { value: 'dos_donts', label: "Do's & Don'ts", icon: AlertCircle },
  { value: 'caption_format', label: 'Caption Formats', icon: FileText },
  { value: 'offer_language', label: 'Offer Language', icon: Tag },
  { value: 'brand_rules', label: 'Brand Rules', icon: Shield },
];

const BrandLibrary = () => {
  const { activeBrand } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('tone');
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    item_type: 'tone',
  });

  useEffect(() => {
    if (activeBrand) {
      fetchItems();
    }
  }, [activeBrand]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await brandLibraryAPI.getAll({}, activeBrand);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch brand library');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      await brandLibraryAPI.create(formData, activeBrand);
      toast.success('Item added!');
      setShowAddSheet(false);
      setFormData({ title: '', content: '', item_type: 'tone' });
      fetchItems();
    } catch (error) {
      toast.error('Failed to create item');
    }
  };

  const handleUpdate = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      await brandLibraryAPI.update(editingItem.id, formData);
      toast.success('Updated!');
      setEditingItem(null);
      setFormData({ title: '', content: '', item_type: 'tone' });
      fetchItems();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      await brandLibraryAPI.delete(id);
      toast.success('Deleted!');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditSheet = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      content: item.content,
      item_type: item.item_type,
    });
  };

  const getItemsByType = (type) => {
    return items.filter(item => item.item_type === type);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="brand-library-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="h-96 bg-zinc-100 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="brand-library-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-medium text-zinc-900"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Brand Library
          </h1>
          <p className="text-zinc-500 mt-1">Guidelines, tone, and brand rules</p>
        </div>
        
        <Button
          onClick={() => {
            setFormData({ title: '', content: '', item_type: activeTab });
            setShowAddSheet(true);
          }}
          className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
          data-testid="add-brand-item-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white border border-zinc-100">
        <TabsList className="w-full justify-start border-b border-zinc-100 bg-transparent p-0 h-auto flex-wrap">
          {ITEM_TYPES.map(({ value, label, icon: Icon }) => (
            <TabsTrigger 
              key={value}
              value={value} 
              className="px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
              data-testid={`tab-${value}`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {ITEM_TYPES.map(({ value, label }) => (
          <TabsContent key={value} value={value} className="p-6" data-testid={`content-${value}`}>
            {getItemsByType(value).length > 0 ? (
              <div className="space-y-4">
                {getItemsByType(value).map((item) => (
                  <div 
                    key={item.id}
                    className="border border-zinc-100 p-6 group hover:border-zinc-200 transition-colors"
                    data-testid={`brand-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-medium text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {item.title}
                      </h3>
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditSheet(item)}
                          className="h-8 w-8"
                          data-testid={`edit-item-${item.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(item.id)}
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          data-testid={`delete-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="prose prose-sm max-w-none text-zinc-600 whitespace-pre-wrap">
                      {item.content}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <BookOpen className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  No {label.toLowerCase()} yet
                </h3>
                <p className="text-zinc-500 mb-6">Add your first {label.toLowerCase()} item</p>
                <Button
                  onClick={() => {
                    setFormData({ title: '', content: '', item_type: value });
                    setShowAddSheet(true);
                  }}
                  className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
                >
                  Add Item
                </Button>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit Sheet */}
      <Sheet 
        open={showAddSheet || !!editingItem} 
        onOpenChange={(open) => {
          if (!open) {
            setShowAddSheet(false);
            setEditingItem(null);
            setFormData({ title: '', content: '', item_type: 'tone' });
          }
        }}
      >
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="brand-item-sheet">
          <SheetHeader className="pb-6 border-b border-zinc-100">
            <SheetTitle className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              {editingItem ? 'Edit Item' : 'Add Item'}
            </SheetTitle>
            <p className="text-sm text-zinc-500 mt-1">Add brand guidelines and references</p>
          </SheetHeader>
          
          <div className="space-y-5 mt-6">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Type
              </label>
              <Select
                value={formData.item_type}
                onValueChange={(value) => setFormData({ ...formData, item_type: value })}
              >
                <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="item-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="Give it a title"
                data-testid="item-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Content <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all min-h-[200px]"
                placeholder="Add your content here..."
                data-testid="item-content-input"
              />
            </div>

            <Button
              onClick={editingItem ? handleUpdate : handleCreate}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-md py-4 text-sm font-medium tracking-wide transition-all mt-4"
              data-testid="save-brand-item-btn"
            >
              {editingItem ? 'Update Item' : 'Add Item'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BrandLibrary;
